import { apiFetch } from '../../utils/apiFetch';
import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import CreateFreeAccountModal from './CreateFreeAccountModal';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  created_at: string;
}

interface FreeAccount {
  id: number;
  user_id: number;
  tenant_id: number;
  account_type: string;
  reason: string;
  notes?: string;
  limit_websites?: string;
  limit_analyses_per_month?: string;
  approved_at?: string;
  approved_by?: number;
  expires_at?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  user?: User;
}

interface Statistics {
  total: number;
  active: number;
  by_type: {
    friend: number;
    leader: number;
    referral_partner: number;
    trial: number;
    other: number;
  };
  expiring_soon: number;
  expired: number;
}

const FreeAccountsList: React.FC = () => {
  const [freeAccounts, setFreeAccounts] = useState<FreeAccount[]>([]);
  const [usersWithoutSub, setUsersWithoutSub] = useState<User[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'accounts' | 'users'>('accounts');
  const [filter, setFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch free accounts
      const accountsResponse = await apiFetch(`/api/admin/free-accounts${filter !== 'all' ? `?type=${filter}` : ''}`, {
        headers: { 'X-Tenant-ID': '1' },
        credentials: 'include'
      });
      if (accountsResponse.ok) {
        const data = await accountsResponse.json();
        setFreeAccounts(data.data || []);
      }

      // Fetch statistics
      const statsResponse = await apiFetch('/api/admin/free-accounts/statistics', {
        headers: { 'X-Tenant-ID': '1' },
        credentials: 'include'
      });
      if (statsResponse.ok) {
        const data = await statsResponse.json();
        setStatistics(data.data);
      }

      // Fetch users without subscriptions
      const usersResponse = await apiFetch('/api/admin/free-accounts/users-without-subscription', {
        headers: { 'X-Tenant-ID': '1' },
        credentials: 'include'
      });
      if (usersResponse.ok) {
        const data = await usersResponse.json();
        setUsersWithoutSub(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const handleApprove = async (accountId: number) => {
    try {
      const response = await apiFetch(`/api/admin/free-accounts/${accountId}/approve`, {
        method: 'POST',
        headers: { 'X-Tenant-ID': '1' },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Free account approved');
        fetchData();
      } else {
        toast.error('Failed to approve account');
      }
    } catch (error) {
      console.error('Error approving account:', error);
      toast.error('Error approving account');
    }
  };

  const handleDelete = async (accountId: number) => {
    if (!confirm('Are you sure you want to delete this free account?')) {
      return;
    }

    try {
      const response = await apiFetch(`/api/admin/free-accounts/${accountId}`, {
        method: 'DELETE',
        headers: { 'X-Tenant-ID': '1' },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Free account deleted');
        fetchData();
      } else {
        toast.error('Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Error deleting account');
    }
  };

  const handleDeactivateExpired = async () => {
    if (!confirm('This will deactivate all expired free accounts. Continue?')) {
      return;
    }

    try {
      const response = await apiFetch('/api/admin/free-accounts/deactivate-expired', {
        method: 'POST',
        headers: { 'X-Tenant-ID': '1' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Deactivated ${data.data.deactivated_count} expired accounts`);
        fetchData();
      } else {
        toast.error('Failed to deactivate expired accounts');
      }
    } catch (error) {
      console.error('Error deactivating accounts:', error);
      toast.error('Error deactivating accounts');
    }
  };

  const filteredUsers = usersWithoutSub.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAccountTypeBadge = (type: string) => {
    const colors: { [key: string]: string } = {
      friend: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      leader: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      referral_partner: 'bg-green-500/20 text-green-400 border-green-500/30',
      trial: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      other: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
    };

    return colors[type] || colors.other;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
            <div className="text-sm text-slate-400">Total</div>
            <div className="text-2xl font-bold text-white mt-1">{statistics.total}</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
            <div className="text-sm text-slate-400">Active</div>
            <div className="text-2xl font-bold text-emerald-400 mt-1">{statistics.active}</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
            <div className="text-sm text-slate-400">Friends</div>
            <div className="text-2xl font-bold text-blue-400 mt-1">{statistics.by_type.friend}</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
            <div className="text-sm text-slate-400">Leaders</div>
            <div className="text-2xl font-bold text-purple-400 mt-1">{statistics.by_type.leader}</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
            <div className="text-sm text-slate-400">Partners</div>
            <div className="text-2xl font-bold text-green-400 mt-1">{statistics.by_type.referral_partner}</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
            <div className="text-sm text-slate-400">Trials</div>
            <div className="text-2xl font-bold text-yellow-400 mt-1">{statistics.by_type.trial}</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
            <div className="text-sm text-slate-400">Expiring</div>
            <div className="text-2xl font-bold text-red-400 mt-1">{statistics.expiring_soon}</div>
          </div>
        </div>
      )}

      {/* View Toggle & Actions */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          <button
            onClick={() => setView('accounts')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
              view === 'accounts'
                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Free Accounts ({freeAccounts.length})
          </button>
          <button
            onClick={() => setView('users')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
              view === 'users'
                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Users Without Subscription ({usersWithoutSub.length})
          </button>
        </div>
        <div className="flex space-x-2">
          {view === 'accounts' && statistics && statistics.expired > 0 && (
            <button
              onClick={handleDeactivateExpired}
              className="px-4 py-2 border border-red-500/30 rounded-xl text-sm font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-all duration-200"
            >
              Deactivate Expired
            </button>
          )}
        </div>
      </div>

      {view === 'accounts' && (
        <>
          {/* Filter */}
          <div className="flex space-x-2">
            {['all', 'friend', 'leader', 'referral_partner', 'trial', 'other'].map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  filter === type
                    ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
              </button>
            ))}
          </div>

          {/* Free Accounts Table */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-700">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">User</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Type</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Reason</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Limits</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Expires</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {freeAccounts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <div className="text-slate-400">
                          <UserIcon className="mx-auto h-12 w-12 mb-2 opacity-50" />
                          <p>No free accounts found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    freeAccounts.map((account) => (
                      <tr key={account.id} className="hover:bg-slate-700/30 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-white">{account.user?.email}</div>
                            <div className="text-sm text-slate-400">
                              {account.user?.first_name} {account.user?.last_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium border ${getAccountTypeBadge(account.account_type)}`}>
                            {account.account_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-300 max-w-xs truncate">{account.reason}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs text-slate-400">
                            {account.limit_websites && <div>Websites: {account.limit_websites}</div>}
                            {account.limit_analyses_per_month && <div>Analyses: {account.limit_analyses_per_month}</div>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-300">{formatDate(account.expires_at)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {account.is_active ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            {!account.approved_at && (
                              <button
                                onClick={() => handleApprove(account.id)}
                                className="text-green-400 hover:text-green-300"
                                title="Approve"
                              >
                                <CheckIcon className="h-5 w-5" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(account.id)}
                              className="text-red-400 hover:text-red-300"
                              title="Delete"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {view === 'users' && (
        <>
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20"
            />
          </div>

          {/* Users Table */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-700">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">User</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Email</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Joined</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <div className="text-slate-400">
                          <UserIcon className="mx-auto h-12 w-12 mb-2 opacity-50" />
                          <p>No users found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-slate-700/30 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-white">
                            {user.first_name} {user.last_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-300">{user.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-400">{formatDate(user.created_at)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setIsModalOpen(true);
                            }}
                            className="inline-flex items-center px-3 py-1 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                            title="Create free account for this user"
                          >
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Create Free Account
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Create Free Account Modal */}
      <CreateFreeAccountModal
        user={selectedUser}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={() => {
          fetchData();
        }}
      />
    </div>
  );
};

export default FreeAccountsList;
